import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { warrantyService } from '../../services/warrantyService';
import { useAuth } from '../../contexts/AuthContext';

const TABS = [
  { key: 'client', label: 'Cliente', icon: 'User' },
  { key: 'vehicles', label: 'Unidades', icon: 'Car' },
  { key: 'services', label: 'Servicios (OTs)', icon: 'Wrench' },
  { key: 'inspections', label: 'Inspecciones', icon: 'Search' },
  { key: 'quotations', label: 'Cotizaciones', icon: 'FileText' },
  { key: 'warranties', label: 'Garantías', icon: 'Shield' },
  { key: 'evidence', label: 'Evidencias', icon: 'Image' }
];

const STATUS_COLORS = {
  draft: 'bg-muted text-muted-foreground', en_proceso: 'bg-primary/10 text-primary',
  aprobada: 'bg-success/10 text-success', cancelada: 'bg-error/10 text-error',
  recepcionado: 'bg-blue-100 text-blue-700', inspeccionado: 'bg-yellow-100 text-yellow-700',
  cotizando: 'bg-orange-100 text-orange-700', aprobado: 'bg-success/10 text-success',
  enproceso: 'bg-primary/10 text-primary', terminado: 'bg-accent/10 text-accent',
  entregado: 'bg-success/10 text-success', activa: 'bg-success/10 text-success',
  vencida: 'bg-error/10 text-error', reclamada: 'bg-warning/10 text-warning'
};

const VehicleArchive = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('client');

  const clientId = location?.state?.clientId || null;
  const vehicleId = location?.state?.vehicleId || null;
  const initialTab = location?.state?.tab || 'client';

  const [client, setClient] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [warranties, setWarranties] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { setActiveTab(initialTab); }, [initialTab]);

  const loadData = useCallback(async () => {
    if (!clientId && !vehicleId) { setLoading(false); return; }
    setLoading(true);
    try {
      let woData = [];
      if (clientId) {
        const [clientResult, vehiclesResult, woResultClient] = await Promise.all([
          supabase?.from('clients')?.select('*')?.eq('id', clientId)?.maybeSingle(),
          supabase?.from('vehicles')?.select('*')?.eq('client_id', clientId)?.order('created_at', { ascending: false }),
          supabase?.from('work_orders')?.select('*, vehicle:vehicles(marca, modelo, year, placas)')?.eq('client_id', clientId)?.order('created_at', { ascending: false })
        ]);
        setClient(clientResult?.data);
        setVehicles(vehiclesResult?.data || []);
        woData = woResultClient?.data || [];
        setWorkOrders(woData);
        const warrantiesData = await warrantyService?.getByClient(clientId);
        setWarranties(warrantiesData || []);
      } else if (vehicleId) {
        const [vehicleResult, woResultVehicle] = await Promise.all([
          supabase?.from('vehicles')?.select('*, client:clients(*)')?.eq('id', vehicleId)?.maybeSingle(),
          supabase?.from('work_orders')?.select('*')?.eq('vehicle_id', vehicleId)?.order('created_at', { ascending: false })
        ]);
        if (vehicleResult?.data) { setVehicles([vehicleResult?.data]); setClient(vehicleResult?.data?.client); }
        woData = woResultVehicle?.data || [];
        setWorkOrders(woData);
        const warrantiesData = await warrantyService?.getByVehicle(vehicleId);
        setWarranties(warrantiesData || []);
      }

      const woIds = woData?.map(w => w?.id)?.filter(Boolean);
      if (woIds?.length > 0) {
        const [inspResult, quotResult] = await Promise.all([
          supabase?.from('technical_inspections')?.select('*, work_order:work_orders(folio_ot)')?.in('work_order_id', woIds)?.order('created_at', { ascending: false }),
          supabase?.from('quotations')?.select('*')?.in('work_order_id', woIds)?.order('created_at', { ascending: false })
        ]);
        setInspections(inspResult?.data || []);
        setQuotations(quotResult?.data || []);
      }
    } catch (err) {
      console.error('[VehicleArchive] Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId, vehicleId]);

  useEffect(() => { loadData(); }, [loadData]);

  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const renderClientTab = () => (
    <div className="space-y-4">
      {client ? (
        <div className="bg-muted/30 rounded-xl p-6 border border-border">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={28} className="text-primary" />
            </div>
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[['Nombre', client?.nombre || client?.name], ['Teléfono', client?.telefono], ['Email', client?.email], ['RFC', client?.rfc], ['Tipo', client?.type || 'individual'], ['Cliente desde', formatDate(client?.created_at)]]?.map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs caption text-muted-foreground">{label}</p>
                  <p className="font-body text-foreground capitalize">{value || '-'}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <Icon name="UserX" size={40} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No se encontró información del cliente</p>
        </div>
      )}
    </div>
  );

  const renderVehiclesTab = () => (
    <div className="space-y-3">
      {vehicles?.length === 0 ? (
        <div className="text-center py-12"><Icon name="Car" size={40} className="text-muted-foreground mx-auto mb-2" /><p className="text-muted-foreground">No hay unidades registradas</p></div>
      ) : vehicles?.map(v => (
        <div key={v?.id} className="bg-muted/30 rounded-xl p-4 border border-border flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><Icon name="Car" size={20} className="text-primary" /></div>
            <div>
              <p className="font-heading font-semibold text-foreground">{v?.marca} {v?.modelo} {v?.year}</p>
              <p className="text-sm caption text-muted-foreground">Placas: {v?.placas || '-'} · VIN: {v?.vin || '-'}</p>
              <p className="text-sm caption text-muted-foreground">Motor: {v?.motor || '-'} · Color: {v?.color || '-'}</p>
              {v?.nickname && <p className="text-xs text-primary mt-1">"{v?.nickname}"</p>}
            </div>
          </div>
          <button onClick={() => navigate('/vehicle-history', { state: { vehicleId: v?.id } })} className="text-xs text-primary hover:underline flex items-center gap-1 flex-shrink-0">
            Historial <Icon name="ArrowRight" size={12} />
          </button>
        </div>
      ))}
    </div>
  );

  const renderServicesTab = () => (
    <div className="space-y-3">
      {workOrders?.length === 0 ? (
        <div className="text-center py-12"><Icon name="ClipboardX" size={40} className="text-muted-foreground mx-auto mb-2" /><p className="text-muted-foreground">No hay órdenes de trabajo</p></div>
      ) : workOrders?.map(wo => (
        <div key={wo?.id} className="bg-muted/30 rounded-xl p-4 border border-border">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-heading font-bold text-primary text-sm data-text">{wo?.folio_ot}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS?.[wo?.estado_ot] || 'bg-muted text-muted-foreground'}`}>{wo?.estado_ot}</span>
              </div>
              <p className="text-sm caption text-muted-foreground">{wo?.vehicle ? `${wo?.vehicle?.marca} ${wo?.vehicle?.modelo} ${wo?.vehicle?.year}` : '-'}</p>
              <p className="text-xs caption text-muted-foreground mt-1">{formatDate(wo?.created_at)}</p>
            </div>
            <button onClick={() => navigate('/quotation-management', { state: { workOrder: { id: wo?.id, folio_ot: wo?.folio_ot } } })} className="text-xs text-primary hover:underline flex items-center gap-1 flex-shrink-0">
              Ver cotización <Icon name="ArrowRight" size={12} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderInspectionsTab = () => (
    <div className="space-y-3">
      {inspections?.length === 0 ? (
        <div className="text-center py-12"><Icon name="SearchX" size={40} className="text-muted-foreground mx-auto mb-2" /><p className="text-muted-foreground">No hay inspecciones registradas</p></div>
      ) : inspections?.map(insp => (
        <div key={insp?.id} className="bg-muted/30 rounded-xl p-4 border border-border">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-heading font-semibold text-foreground capitalize">{insp?.category}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS?.[insp?.status] || 'bg-muted text-muted-foreground'}`}>{insp?.status}</span>
              </div>
              <p className="text-sm caption text-muted-foreground">OT: {insp?.work_order?.folio_ot || insp?.work_order_id?.slice(0, 8)}</p>
              {insp?.findings && <p className="text-xs caption text-muted-foreground mt-1 line-clamp-2">{insp?.findings}</p>}
              <p className="text-xs caption text-muted-foreground mt-1">{formatDate(insp?.created_at)}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderQuotationsTab = () => (
    <div className="space-y-3">
      {quotations?.length === 0 ? (
        <div className="text-center py-12"><Icon name="FileX" size={40} className="text-muted-foreground mx-auto mb-2" /><p className="text-muted-foreground">No hay cotizaciones registradas</p></div>
      ) : quotations?.map(q => (
        <div key={q?.id} className="bg-muted/30 rounded-xl p-4 border border-border">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="font-heading font-bold text-primary text-sm">{q?.folio_cotizacion || q?.id?.slice(0, 8)}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS?.[q?.status] || 'bg-muted text-muted-foreground'}`}>{q?.status}</span>
              </div>
              <p className="text-sm caption text-muted-foreground">Total: ${(q?.total || 0)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
              <p className="text-xs caption text-muted-foreground mt-1">{formatDate(q?.created_at)}</p>
            </div>
            <button onClick={() => navigate('/quotation-management', { state: { workOrder: { id: q?.work_order_id } } })} className="text-xs text-primary hover:underline flex items-center gap-1 flex-shrink-0">
              Abrir <Icon name="ArrowRight" size={12} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderWarrantiesTab = () => (
    <div className="space-y-3">
      {warranties?.length === 0 ? (
        <div className="text-center py-12"><Icon name="ShieldOff" size={40} className="text-muted-foreground mx-auto mb-2" /><p className="text-muted-foreground">No hay garantías registradas</p></div>
      ) : warranties?.map(w => (
        <div key={w?.id} className="bg-muted/30 rounded-xl p-4 border border-border">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-heading font-semibold text-foreground capitalize">{w?.warrantyType?.replace('_', ' ')}</span>
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS?.[w?.status] || 'bg-muted text-muted-foreground'}`}>{w?.status}</span>
          </div>
          <p className="text-sm caption text-muted-foreground">Vigencia: {formatDate(w?.startDate)} — {formatDate(w?.endDate)}</p>
          {w?.notes && <p className="text-xs caption text-muted-foreground mt-1">{w?.notes}</p>}
        </div>
      ))}
    </div>
  );

  const renderEvidenceTab = () => (
    <div className="space-y-4">
      {inspections?.filter(i => i?.photos?.length > 0)?.length === 0 ? (
        <div className="text-center py-12"><Icon name="ImageOff" size={40} className="text-muted-foreground mx-auto mb-2" /><p className="text-muted-foreground">No hay evidencias fotográficas registradas</p></div>
      ) : inspections?.filter(i => i?.photos?.length > 0)?.map(insp => (
        <div key={insp?.id}>
          <p className="text-sm font-heading font-semibold text-foreground mb-2 capitalize">{insp?.category} — {formatDate(insp?.created_at)}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {(insp?.photos || [])?.map((photo, idx) => (
              <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-muted border border-border">
                <img src={photo?.url || photo} alt={`Evidencia ${insp?.category} ${idx + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const TAB_RENDERERS = { client: renderClientTab, vehicles: renderVehiclesTab, services: renderServicesTab, inspections: renderInspectionsTab, quotations: renderQuotationsTab, warranties: renderWarrantiesTab, evidence: renderEvidenceTab };

  return (
    <>
      <Helmet><title>Archivo del Vehículo - GearControl</title></Helmet>
      <div className="min-h-screen bg-background">
        <MainSidebar isCollapsed={sidebarCollapsed} userRole="admin" />
        <div className={`transition-smooth ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          <header className="sticky top-0 z-40 bg-card border-b border-border shadow-elevation-1">
            <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth press-effect focus-ring flex-shrink-0">
                  <Icon name={sidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={20} />
                </button>
                <button onClick={() => navigate(-1)} className="hidden lg:flex p-2 rounded-lg hover:bg-muted transition-smooth flex-shrink-0">
                  <Icon name="ArrowLeft" size={20} className="text-muted-foreground" />
                </button>
                <div className="min-w-0 pl-10 lg:pl-0">
                  <h1 className="text-xl font-heading font-bold text-foreground leading-tight">Archivo del Vehículo</h1>
                  <p className="text-sm caption text-muted-foreground mt-1">
                    {client?.nombre || client?.name || 'Historial completo'}
                    {vehicles?.[0] ? ` · ${vehicles?.[0]?.marca} ${vehicles?.[0]?.modelo}` : ''}
                  </p>
                </div>
              </div>
              <Button variant="outline" iconName="History" iconPosition="left" onClick={() => navigate('/vehicle-history', { state: { clientId, vehicleId } })} className="flex-shrink-0">
                <span className="hidden sm:inline">Ver Timeline</span>
                <span className="sm:hidden sr-only">Timeline</span>
              </Button>
            </div>
          </header>

          <main className="p-4 md:p-6 lg:p-8">
            {loading ? (
              <div className="flex items-center justify-center py-24 gap-2">
                <Icon name="Loader2" size={24} className="text-primary animate-spin" />
                <span className="text-muted-foreground">Cargando archivo...</span>
              </div>
            ) : !clientId && !vehicleId ? (
              <div className="text-center py-24">
                <Icon name="FolderOpen" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-lg font-heading font-semibold text-foreground mb-2">Sin contexto</p>
                <p className="text-muted-foreground mb-4">Navega desde una OT, cliente o vehículo para ver el archivo</p>
                <Button variant="default" onClick={() => navigate('/vehicle-history')}>Ir a Historial</Button>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[{ label: 'Unidades', value: vehicles?.length, icon: 'Car', color: 'text-primary' }, { label: 'Servicios', value: workOrders?.length, icon: 'Wrench', color: 'text-accent' }, { label: 'Inspecciones', value: inspections?.length, icon: 'Search', color: 'text-warning' }, { label: 'Garantías', value: warranties?.length, icon: 'Shield', color: 'text-success' }]?.map(stat => (
                    <div key={stat?.label} className="bg-card rounded-xl border border-border p-4 flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-muted"><Icon name={stat?.icon} size={18} className={stat?.color} /></div>
                      <div>
                        <p className="text-xl font-heading font-bold text-foreground">{stat?.value}</p>
                        <p className="text-xs caption text-muted-foreground">{stat?.label}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  <div className="flex overflow-x-auto border-b border-border">
                    {TABS?.map(tab => (
                      <button key={tab?.key} onClick={() => setActiveTab(tab?.key)}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-smooth border-b-2 ${activeTab === tab?.key ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}>
                        <Icon name={tab?.icon} size={16} />{tab?.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-4 md:p-6">{TAB_RENDERERS?.[activeTab]?.()}</div>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
};

export default VehicleArchive;
